import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Send, FileText, ChevronDown, Bell, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const SOURCES = ['ANMAT', 'Boletín Oficial', 'SENASA', 'Min. Salud', 'PAMI', 'INAME'];
interface SearchWidgetProps {
  mode?: 'full' | 'compact';
}
export function SearchWidget({
  mode = 'full'
}: SearchWidgetProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [alertCreated, setAlertCreated] = useState<number | null>(null);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const toggleSource = (source: string) => {
    setSelectedSources(prev => prev.includes(source) ? prev.filter(s => s !== source) : [...prev, source]);
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (mode === 'compact') {
      const params = new URLSearchParams({
        q: query
      });
      if (selectedSources.length > 0) params.set('sources', selectedSources.join(','));
      navigate(`/busquedas?${params.toString()}`);
      return;
    }
    setIsSearching(true);
    setHasSearched(true);
    setTimeout(() => setIsSearching(false), 1500);
  };
  const handleSetSuggestion = (text: string) => {
    if (mode === 'compact') {
      navigate(`/busquedas?q=${encodeURIComponent(text)}`);
    } else {
      setQuery(text);
    }
  };
  const handleCreateAlert = (resultId: number) => {
    setAlertCreated(resultId);
    setTimeout(() => setAlertCreated(null), 3000);
  };
  const mockResults = [{
    id: 1,
    source: 'ANMAT',
    code: 'Disposición 1234/2023',
    date: '12 Oct 2023',
    title: 'Actualización de requisitos para importación de principios activos',
    snippet: 'Establécese que los laboratorios importadores deberán presentar la documentación adicional detallada en el Anexo I para la liberación de lotes de principios activos destinados a...'
  }, {
    id: 2,
    source: 'ANMAT',
    code: 'Disposición 1235/2023',
    date: '10 Oct 2023',
    title: 'Modificación del régimen de trazabilidad para medicamentos oncológicos',
    snippet: 'Modifícase la Disposición 2303/2014 en lo referente a los plazos de implementación del sistema de trazabilidad para especialidades medicinales de uso oncológico...'
  }, {
    id: 3,
    source: 'Boletín Oficial',
    code: 'Resolución 456/2023',
    date: '08 Oct 2023',
    title: 'Nuevos estándares de calidad para principios activos importados',
    snippet: 'Apruébanse los nuevos estándares de control de calidad aplicables a la importación de principios activos farmacéuticos según las recomendaciones de la OMS...'
  }];
  function SourceTags({
    size = 'sm'
  }: {
    size?: 'sm' | 'xs';
  }) {
    return <div className="flex flex-wrap gap-1.5" data-id="element-63">
        {(size === 'xs' ? SOURCES.slice(0, 4) : SOURCES).map(source => <button key={source} type="button" onClick={() => toggleSource(source)} className={`px-2 py-1 rounded-md font-medium transition-all border ${size === 'xs' ? 'text-[10px]' : 'text-xs'} ${selectedSources.includes(source) ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-navy-600 border-slate-200 hover:border-brand-300'}`} data-id="element-64">
            {source}
          </button>)}
        {selectedSources.length > 0 && <button type="button" onClick={() => setSelectedSources([])} className={`px-2 py-1 rounded-md font-medium text-slate-400 hover:text-navy-950 transition-colors ${size === 'xs' ? 'text-[10px]' : 'text-xs'}`} data-id="element-65">
            Limpiar
          </button>}
      </div>;
  }
  // Compact mode — dashboard widget
  if (mode === 'compact') {
    return <div className="bg-white rounded-xl border border-slate-200 shadow-card p-5" data-id="element-66">
        <div className="flex items-center gap-2 mb-4" data-id="element-67">
          <Sparkles size={18} className="text-brand-500" data-id="element-68" />
          <h3 className="text-sm font-medium text-navy-950" data-id="element-69">
            Búsqueda Semántica
          </h3>
        </div>

        <form onSubmit={handleSearch} className="relative mb-3" data-id="element-70">
          <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar normativas, disposiciones..." className="w-full h-10 pl-3 pr-10 rounded-md border border-slate-200 bg-white text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" data-id="element-71" />
          <button type="submit" disabled={!query.trim()} className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 bg-brand-500 hover:bg-brand-400 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-md flex items-center justify-center transition-colors" data-id="element-72">
            <Send size={12} data-id="element-73" />
          </button>
        </form>

        <div className="mb-4" data-id="element-74">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-1.5" data-id="element-75">
            Fuentes
          </p>
          <SourceTags size="xs" data-id="element-76" />
        </div>

        <div className="space-y-2" data-id="element-77">
          <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold" data-id="element-78">
            Sugerencias
          </p>
          {['Disposiciones ANMAT recientes', 'Cambios en trazabilidad'].map(suggestion => <button key={suggestion} onClick={() => handleSetSuggestion(suggestion)} className="w-full text-left px-3 py-2 rounded-md border border-slate-100 hover:border-brand-300 hover:bg-brand-50/30 transition-all text-xs text-navy-600" data-id="element-79">
                "{suggestion}"
              </button>)}
        </div>
      </div>;
  }
  // Full mode — Búsquedas page
  return <div className="flex flex-col h-full" data-id="element-80">
      {/* Alert created toast */}
      <AnimatePresence data-id="element-81">
        {alertCreated !== null && <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-navy-950 text-white px-5 py-3 rounded-lg shadow-dropdown flex items-center gap-3 text-sm" data-id="element-82">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shrink-0" data-id="element-83">
              <Check size={12} data-id="element-84" />
            </div>
            Alerta semántica creada exitosamente. Se monitoreará esta búsqueda.
          </motion.div>}
      </AnimatePresence>

      {/* Chat / Search Area */}
      <div className="flex-1 overflow-y-auto p-6 lg:p-8" data-id="element-85">
        {!hasSearched ? <div className="h-full flex flex-col items-center justify-center text-center max-w-2xl mx-auto space-y-6" data-id="element-86">
            <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-2" data-id="element-87">
              <Sparkles size={32} className="text-brand-500" data-id="element-88" />
            </div>
            <h1 className="text-2xl text-navy-950 font-light" data-id="element-89">
              Búsqueda Semántica Agentica
            </h1>
            <p className="text-navy-600 text-base" data-id="element-90">
              Describa lo que está buscando en lenguaje natural. Nuestro agente
              analizará el contexto de miles de documentos oficiales para
              encontrar exactamente lo que necesita.
            </p>

            {/* Source filters in empty state */}
            <div className="w-full" data-id="element-91">
              <p className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold mb-2" data-id="element-92">
                Filtrar por fuente
              </p>
              <div className="flex justify-center" data-id="element-93">
                <SourceTags size="sm" data-id="element-94" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full mt-4" data-id="element-95">
              <button onClick={() => setQuery('Resoluciones sobre importación de principios activos oncológicos en 2023')} className="text-left p-4 rounded-xl border border-slate-200 hover:border-brand-300 hover:bg-brand-50/50 transition-all text-sm text-navy-800" data-id="element-96">
                "Resoluciones sobre importación de principios activos
                oncológicos en 2023"
              </button>
              <button onClick={() => setQuery('Cambios en normativas de trazabilidad de medicamentos de venta libre')} className="text-left p-4 rounded-xl border border-slate-200 hover:border-brand-300 hover:bg-brand-50/50 transition-all text-sm text-navy-800" data-id="element-97">
                "Cambios en normativas de trazabilidad de medicamentos de venta
                libre"
              </button>
            </div>
          </div> : <div className="space-y-8 pb-20 max-w-5xl mx-auto" data-id="element-98">
            {/* Active source filters */}
            {selectedSources.length > 0 && <div className="flex items-center gap-2 flex-wrap" data-id="element-99">
                <span className="text-xs text-slate-400" data-id="element-100">Filtrando por:</span>
                {selectedSources.map(s => <span key={s} className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 bg-brand-50 px-2 py-1 rounded-md border border-brand-100" data-id="element-101">
                    {s}
                    <button onClick={() => toggleSource(s)} className="text-brand-400 hover:text-brand-600 ml-0.5" data-id="element-102">
                      ×
                    </button>
                  </span>)}
              </div>}

            {/* User Query */}
            <div className="flex justify-end" data-id="element-103">
              <div className="bg-navy-950 text-white px-5 py-3 rounded-2xl rounded-tr-sm max-w-2xl text-sm" data-id="element-104">
                {query}
              </div>
            </div>

            {/* Agent Response */}
            <div className="flex items-start gap-4" data-id="element-105">
              <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center shrink-0 mt-1" data-id="element-106">
                <Sparkles size={14} className="text-white" data-id="element-107" />
              </div>
              <div className="flex-1 space-y-4" data-id="element-108">
                {isSearching ? <div className="bg-white border border-slate-200 px-5 py-4 rounded-2xl rounded-tl-sm w-fit flex items-center gap-3 text-sm text-navy-600" data-id="element-109">
                    <div className="flex gap-1" data-id="element-110">
                      <div className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-111" />
                      <div className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-112" />
                      <div className="w-1.5 h-1.5 bg-brand-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-113" />
                    </div>
                    Analizando contexto en bases de datos oficiales...
                  </div> : <>
                    <div className="bg-white border border-slate-200 px-5 py-4 rounded-2xl rounded-tl-sm text-sm text-navy-800 leading-relaxed" data-id="element-114">
                      He encontrado{' '}
                      <span className="font-semibold" data-id="element-115">
                        3 documentos relevantes
                      </span>{' '}
                      que coinciden con el contexto de su búsqueda
                      {selectedSources.length > 0 && <>
                          {' '}
                          en{' '}
                          <span className="font-semibold" data-id="element-116">
                            {selectedSources.join(', ')}
                          </span>
                        </>}
                      . Puede crear una alerta semántica para monitorear esta
                      búsqueda de forma continua.
                    </div>

                    {/* Create alert from search CTA */}
                    <button onClick={() => handleCreateAlert(0)} className="flex items-center gap-2 bg-brand-50 hover:bg-brand-100 border border-brand-200 text-brand-600 text-sm font-medium px-4 py-2.5 rounded-lg transition-colors w-fit" data-id="element-117">
                      <Bell size={14} data-id="element-118" />
                      Crear alerta semántica desde esta búsqueda
                    </button>

                    {/* Results */}
                    <div className="space-y-3" data-id="element-119">
                      {mockResults.map(result => <div key={result.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:border-brand-300 transition-colors group" data-id="element-120">
                          <div className="flex items-start justify-between gap-4 mb-2" data-id="element-121">
                            <div className="flex items-center gap-2 text-xs font-medium text-brand-600 bg-brand-50 px-2 py-1 rounded-md w-fit" data-id="element-122">
                              <FileText size={12} data-id="element-123" />
                              {result.source} - {result.code}
                            </div>
                            <div className="flex items-center gap-2 shrink-0" data-id="element-124">
                              <span className="text-xs text-slate-400" data-id="element-125">
                                {result.date}
                              </span>
                              <button onClick={() => handleCreateAlert(result.id)} className="text-slate-300 hover:text-brand-500 transition-colors p-1 rounded-md hover:bg-brand-50 opacity-0 group-hover:opacity-100 focus:opacity-100" title="Crear alerta desde este resultado" data-id="element-126">
                                <Bell size={14} data-id="element-127" />
                              </button>
                            </div>
                          </div>
                          <h3 className="text-sm font-medium text-navy-950 mb-1 group-hover:text-brand-600 transition-colors cursor-pointer" data-id="element-128">
                            {result.title}
                          </h3>
                          <p className="text-xs text-navy-600 line-clamp-2" data-id="element-129">
                            {result.snippet}
                          </p>
                        </div>)}
                    </div>

                    <div className="flex justify-center pt-4" data-id="element-130">
                      <button className="text-xs font-medium text-slate-400 hover:text-navy-950 flex items-center gap-1 transition-colors" data-id="element-131">
                        Cargar más resultados <ChevronDown size={14} data-id="element-132" />
                      </button>
                    </div>
                  </>}
              </div>
            </div>
          </div>}
      </div>

      {/* Input Area */}
      <div className="p-4 lg:p-6 bg-slate-50 border-t border-slate-200 shrink-0" data-id="element-133">
        <form onSubmit={handleSearch} className="max-w-4xl mx-auto" data-id="element-134">
          {/* Source tags row above input */}
          <div className="mb-2" data-id="element-135">
            <SourceTags size="sm" data-id="element-136" />
          </div>
          <div className="relative" data-id="element-137">
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Pregunte a nuestro agente sobre normativas, disposiciones o resoluciones..." className="w-full h-14 pl-5 pr-14 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 shadow-sm" data-id="element-138" />
            <button type="submit" disabled={!query.trim() || isSearching} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-brand-500 hover:bg-brand-400 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-lg flex items-center justify-center transition-colors" data-id="element-139">
              <Send size={16} data-id="element-140" />
            </button>
          </div>
        </form>
        <div className="text-center mt-2" data-id="element-141">
          <span className="text-[10px] text-slate-400" data-id="element-142">
            La IA puede cometer errores. Verifique siempre la información en los
            boletines oficiales.
          </span>
        </div>
      </div>
    </div>;
}